import type {
  CountryNames,
  IpHandlingNames,
  PurposeNames,
  RetentionBasisNames,
  TransferBasisNames,
} from '../types';

/** Fact → English prose. See `content/types.ts` for why these are keyed records. */

export const COUNTRY: CountryNames = {
  DE: 'Germany',
  NL: 'Netherlands',
  US: 'United States',
  PENDING: 'region not yet confirmed',
};

export const PURPOSE: PurposeNames = {
  'app-hosting': 'hosting for the API, routing service and dashboard',
  'site-hosting': 'hosting for this website',
  'mail-infrastructure': 'the server that receives forwarded mail',
  'tls-termination': 'TLS certificates and traffic termination for custom domains',
  'transactional-email': 'transactional email (verification, password reset, forwarding relay)',
  'object-storage': 'storage for generated QR codes and uploaded assets',
  'bot-mitigation': 'bot detection for form submissions',
  'ai-assistant': 'the AI model behind the dashboard assistant',
  'url-safety': 'checking link destinations for malware and phishing',
  'operator-alerts': 'internal alerts to our team about abuse reports and unsafe links',
};

export const TRANSFER_BASIS: TransferBasisNames = {
  scc: 'EU standard contractual clauses (Art. 46(2)(c) GDPR)',
  adequacy: 'an adequacy decision (Art. 45 GDPR)',
};

/**
 * How visitor IP addresses are handled. Keep the previous-state variant so a
 * rollback can remain legally honest without rewriting prose under pressure.
 */
export const IP_HANDLING: IpHandlingNames = {
  'anonymised-at-ingestion':
    'The IP address is truncated before durable storage to its /24 IPv4 or /48 IPv6 network prefix and never stored in full; it serves only to derive the country of origin and to detect abuse.',
  'stored-in-full':
    'The full IP address is currently stored. We are working to anonymise it on arrival.',
};

/**
 * How long click and analytics records are kept. Two variants because #376
 * changes the present state: no automated deletion today, plan-based windows
 * afterwards.
 */
export const RETENTION_BASIS: RetentionBasisNames = {
  'account-lifetime':
    'This data is kept for as long as the account exists. Automated, plan-based deletion windows are in preparation; until then you can request erasure at any time.',
  'plan-based':
    'This data is kept for the window attached to your plan and deleted automatically thereafter.',
};

/** "a, b and c" — the conjunction is language-specific, the names are not. */
export const joinNames = (names: readonly string[]): string =>
  new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(
    names as string[]
  );
